import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Text } from '@kognitos/lattice';

import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import type { IResolvedStepConfig } from '@/modules/automation/utils/resolve-step-config';

import { ManifestConfigValueEditor } from './ManifestConfigValueEditor';

type TSaveStatus = 'idle' | 'saving' | 'error';

interface IConfigValueRowProps {
  config: IResolvedStepConfig;
  onSave: (name: string, value: Commonv1Value) => Promise<void>;
}

/** One editable config row. Owns the last-known-good value and save status;
 *  on a failed save the editor remounts (`resetToken`) back to that value so
 *  a rejected edit can't leave the field showing something that was never
 *  actually persisted. */
function ConfigValueRow({ config, onSave }: IConfigValueRowProps) {
  const { t: tAutomation } = useTranslation('automation');
  const [committedValue, setCommittedValue] = useState(config.value);
  const [status, setStatus] = useState<TSaveStatus>('idle');
  const [resetToken, setResetToken] = useState(0);

  const handleCommit = async (newValue: Commonv1Value) => {
    setStatus('saving');
    try {
      await onSave(config.name, newValue);
      setCommittedValue(newValue);
      setStatus('idle');
    } catch {
      setStatus('error');
      setResetToken(token => token + 1);
    }
  };

  return (
    <Flex direction="column" align="stretch" gap={1} width="full">
      <Text level="xSmall" color="muted" weight="medium">
        {config.label}
      </Text>
      <ManifestConfigValueEditor
        key={resetToken}
        name={`config-${config.name}`}
        label={config.label}
        value={committedValue}
        disabled={status === 'saving'}
        onCommit={handleCommit}
      />
      {status === 'error' && (
        <Text level="xSmall" color="destructive">
          {tAutomation('automation.manifestSopView.config.saveError')}
        </Text>
      )}
    </Flex>
  );
}

export interface IManifestConfigTabProps {
  configs: IResolvedStepConfig[];
  onSave: (name: string, value: Commonv1Value) => Promise<void>;
}

/** A step's business-tunable configs, resolved from `audit.config_accesses`
 *  against the automation's config map. Each row edits and saves
 *  independently — see `ConfigValueRow`. */
export function ManifestConfigTab({
  configs,
  onSave,
}: IManifestConfigTabProps) {
  return (
    <Flex direction="column" align="stretch" gap={4} width="full">
      {configs.map(config => (
        <ConfigValueRow key={config.name} config={config} onSave={onSave} />
      ))}
    </Flex>
  );
}
